import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatDate } from "../../lib/format";

const fmt = (n) => Number(n || 0).toLocaleString("en-US");

export function Panel({ title, subtitle, children, testid, className = "" }) {
  return (
    <section className={`bg-surface border border-neutral-200 rounded-xl p-6 min-w-0 ${className}`} data-testid={testid}>
      <div className="mb-5">
        <h2 className="font-playfair text-xl text-neutral-900">{title}</h2>
        {subtitle && <p className="text-xs text-neutral-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

export function StatCard({ label, period, data, testid }) {
  return (
    <div className="bg-surface border border-neutral-200 rounded-xl p-6 min-w-0" data-testid={testid}>
      <p className="eyebrow mb-1">{label}</p>
      <p className="text-xs text-neutral-500 mb-4">{period}</p>
      <p className="font-playfair text-4xl text-neutral-900 leading-none" data-testid={`${testid}-visitors`}>{fmt(data?.visitors)}</p>
      <p className="text-xs text-neutral-500 mt-2">visitors</p>
      <div className="mt-5 pt-4 border-t border-neutral-200 grid grid-cols-2 gap-3 text-sm">
        <div><p className="font-semibold text-neutral-900" data-testid={`${testid}-views`}>{fmt(data?.page_views)}</p><p className="text-xs text-neutral-500">page views</p></div>
        <div><p className="font-semibold text-neutral-900">{fmt(data?.sessions)}</p><p className="text-xs text-neutral-500">sessions</p></div>
      </div>
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-neutral-200 rounded-md px-3 py-2 shadow-sm text-xs">
      <p className="font-semibold text-neutral-900 mb-1">{formatDate(label)}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-neutral-600">{p.name}: <span className="font-semibold text-neutral-900">{fmt(p.value)}</span></p>
      ))}
    </div>
  );
}

export function TrendChart({ data }) {
  return (
    <div className="h-64 -ml-2" data-testid="analytics-trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gV" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4038B5" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#4038B5" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#DDDAD2" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#666779" }} tickLine={false} axisLine={false} minTickGap={24} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#666779" }} tickLine={false} axisLine={false} width={36} />
          <Tooltip content={<ChartTip />} />
          <Area type="monotone" dataKey="page_views" name="Page views" stroke="#A6A0DD" strokeWidth={1.5} fill="none" />
          <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#4038B5" strokeWidth={2} fill="url(#gV)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function BarList({ items, valueKey, labelKey = "name", sublabelKey, unit, testid, empty = "No data for this period yet." }) {
  if (!items?.length) return <p className="text-sm text-neutral-500" data-testid={`${testid}-empty`}>{empty}</p>;
  const max = Math.max(...items.map((i) => i[valueKey]), 1);
  return (
    <ul className="space-y-3" data-testid={testid}>
      {items.map((item, idx) => (
        <li key={`${item[labelKey]}-${idx}`} className="min-w-0">
          <div className="flex items-baseline justify-between gap-3 text-sm mb-1.5">
            <span className="truncate text-neutral-800" title={item[labelKey]}>
              {item[labelKey] || "(not set)"}
              {sublabelKey && item[sublabelKey] && <span className="ml-2 text-xs text-neutral-500">{item[sublabelKey]}</span>}
            </span>
            <span className="shrink-0 font-semibold text-neutral-900 tabular-nums">{fmt(item[valueKey])}{unit && <span className="text-xs font-normal text-neutral-500"> {unit}</span>}</span>
          </div>
          <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
            <div className="h-full rounded-full bg-indigo-800/80" style={{ width: `${(item[valueKey] / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export { fmt };
